import { Injectable } from '@angular/core';
import * as _ from 'lodash';
import { LoadingSpaceType } from 'src/app/core/services/import-export-service-types';
import { ProjectService } from 'src/app/core/services/project.service';
import {
  cargoSpaceHasLoadedPalletLayers,
  visualizationGroupsForCargoSpace,
} from 'src/app/shared/helpers/pallet-loading-space.helper';
import { Project } from '../objects/project';
import {
  Axle,
  CargoSpace,
  CargoSpaceSummary,
  Group,
  OutsideSpace,
  WorkSpace,
} from './../types';

@Injectable({
  providedIn: 'root',
})
export class CargoSpacesSummaryService {
  public cargoSpacesSummary: CargoSpaceSummary[];
  public workSpacesSummary;
  private unit: string;
  private project: Project;

  constructor(private projectService: ProjectService) {
    this.projectService.project.subscribe((project) => {
      this.cargoSpacesSummary = [];
      this.project = project;
      if (project.workSpaces.length === 0) {
        return;
      }
      this.cargoSpacesSummary =
        this.projectService.getCurrentStopIndex() >= 0
          ? this.calculateCargoSpacesSummary(
              this.projectService.getCurrentWorkSpace(),
              this.projectService.getCurrentStopIndex(),
            )
          : this.calculateCargoSpacesSummary(this.projectService.getCurrentWorkSpace());

      this.workSpacesSummary = this.calculateWorkSpacesSummary();
    });
  }

  public calculateWorkSpacesSummary(project?: Project) {
    const projectToCalculate = project ? project : this.project;
    let freeLdm = 0;
    let takenLdm = 0;
    let loadedLoads = 0;
    let notLoadedLoads = 0;
    let occupiedVolume = 0;
    let freeVolume = 0;
    let totalVolume = 0;
    let totalWeight = 0;
    let freeArea = 0;

    this.unit = projectToCalculate.options?.unit;

    if (this.projectService.getCurrentStopIndex() >= 0) {
      const currentWorkSpace = this.projectService.getCurrentWorkSpace();
      projectToCalculate.workSpaces[currentWorkSpace].stops.forEach((_stop, index) => {
        notLoadedLoads += this.countNotLoadedLoads(
          projectToCalculate.workSpaces[currentWorkSpace].stops[index].outsideSpace,
        );
        this.calculateCargoSpacesSummary(currentWorkSpace, index).forEach(
          (cargoSpace: CargoSpaceSummary) => {
            freeLdm += cargoSpace.freeLdm;
            takenLdm += cargoSpace.takenLdm;
            occupiedVolume += cargoSpace.occupiedVolume;
            freeArea += cargoSpace.freeArea;
            freeVolume += cargoSpace.freeVolume;
            totalVolume += cargoSpace.totalVolume;
            loadedLoads += cargoSpace.loadedLoads;
            totalWeight += cargoSpace.totalWeight;
          },
        );
      });
    } else {
      projectToCalculate.workSpaces.forEach((_workSpace, index) => {
        notLoadedLoads += this.countNotLoadedLoads(
          projectToCalculate.workSpaces[index].outsideSpace,
        );
        this.calculateCargoSpacesSummary(index).forEach((cargoSpace: CargoSpaceSummary) => {
          freeLdm += cargoSpace.freeLdm;
          takenLdm += cargoSpace.takenLdm;
          loadedLoads += cargoSpace.loadedLoads;
          occupiedVolume += cargoSpace.occupiedVolume;
          totalWeight += cargoSpace.totalWeight;
          freeArea += cargoSpace.freeArea;
          freeVolume += cargoSpace.freeVolume;
          totalVolume += cargoSpace.totalVolume;
        });
      });
    }

    return {
      freeLdm: this.round(freeLdm),
      takenLdm: this.round(takenLdm),
      loadedLoads: this.round(loadedLoads),
      notLoadedLoads: this.round(notLoadedLoads),
      occupiedVolume: this.round(occupiedVolume),
      freeVolume: this.round(freeVolume),
      totalVolume: this.round(totalVolume),
      totalWeight: this.round(totalWeight),
      freeArea: this.round(freeArea),
    };
  }

  public calculateCargoSpacesSummary(
    workSpaceIndex: number,
    stopIndex?: number,
    project?: Project,
    onlyLoaded?: boolean,
  ): CargoSpaceSummary[] {
    this.unit = this.project.options.unit;
    const cargoSpacesSummaryTemporary: CargoSpaceSummary[] = [];
    const projectToCalculate = project ? project : this.project;
    if (stopIndex >= 0) {
      projectToCalculate.workSpaces[workSpaceIndex].stops[stopIndex].cargoSpaces.forEach(
        (cargoSpace, idx) => {
          const axis: Axle[] = this.prepareAxis(projectToCalculate.workSpaces[workSpaceIndex], idx);
          cargoSpacesSummaryTemporary.push({
            freeLdm: this.round(this.calculateFreeLdm(cargoSpace)),
            takenLdm: this.round(this.calculateTakenLdm(cargoSpace)),
            loadedLoads: this.countLoadedLoads(
              cargoSpace,
              projectToCalculate.workSpaces[workSpaceIndex].stops,
              stopIndex,
              onlyLoaded,
            ),
            notLoadedLoads: this.countNotLoadedLoads(
              projectToCalculate.workSpaces[workSpaceIndex].stops[stopIndex].outsideSpace,
            ),
            occupiedVolume: this.round(this.calculateOccupiedVolume(cargoSpace)),
            freeVolume: this.round(
              this.calculateTotalVolume(cargoSpace) - this.calculateOccupiedVolume(cargoSpace),
            ),
            totalVolume: this.round(this.calculateTotalVolume(cargoSpace)),
            totalWeight: this.round(this.calculateTotalWeight(cargoSpace)),
            freeArea: this.round(this.calculateFreeArea(cargoSpace)),
            axis,
            freeLdmVarioUp:
              cargoSpace.ldmVarioUp >= 0
                ? this.round(this.calculateFreeLdmVarioUp(cargoSpace))
                : undefined,
            takenLdmVarioUp:
              cargoSpace.ldmVarioUp >= 0
                ? this.round(this.calculateTakenLdmVarioUp(cargoSpace))
                : undefined,
          });
        },
      );
    } else {
      projectToCalculate.workSpaces[workSpaceIndex]?.cargoSpaces.forEach(
        (cargoSpace: CargoSpace, index): void => {
          const axis: Axle[] = this.prepareAxis(
            projectToCalculate.workSpaces[workSpaceIndex],
            index,
          );
          cargoSpacesSummaryTemporary.push({
            freeLdm: this.round(this.calculateFreeLdm(cargoSpace)),
            takenLdm: this.round(this.calculateTakenLdm(cargoSpace)),
            loadedLoads: this.countLoadedLoads(cargoSpace, undefined, workSpaceIndex),
            notLoadedLoads: this.countNotLoadedLoads(
              projectToCalculate.workSpaces[workSpaceIndex].outsideSpace,
            ),
            occupiedVolume: this.round(this.calculateOccupiedVolume(cargoSpace)),
            freeVolume: this.round(
              this.calculateTotalVolume(cargoSpace) - this.calculateOccupiedVolume(cargoSpace),
            ),
            totalVolume: this.round(this.calculateTotalVolume(cargoSpace)),
            totalWeight: this.round(this.calculateTotalWeight(cargoSpace)),
            freeArea: this.round(this.calculateFreeArea(cargoSpace)),
            axis,
            // frontAxisLoad: cargoSpace.frontAxi
            //   ? this.round(this.calculateAxiLoad(cargoSpace.frontAxi))
            //   : undefined,
            // rearAxisLoad: cargoSpace.rearAxi
            //   ? this.round(this.calculateAxiLoad(cargoSpace.rearAxi))
            //   : undefined,
            freeLdmVarioUp:
              cargoSpace.ldmVarioUp >= 0
                ? this.round(this.calculateFreeLdmVarioUp(cargoSpace))
                : undefined,
            takenLdmVarioUp:
              cargoSpace.ldmVarioUp >= 0
                ? this.round(this.calculateTakenLdmVarioUp(cargoSpace))
                : undefined,
          });
        },
      );
    }
    return cargoSpacesSummaryTemporary;
  }

  private prepareAxis(workSpace: WorkSpace, index: number): Axle[] {
    if (!workSpace.axis) {
      return [];
    }
    const filteredAxis: Axle[] = workSpace.axis.filter((axi) => axi.loadingSpacePart === index + 1);
    return filteredAxis;
  }

  public calculateFreeArea(cargoSpace: CargoSpace): number {
    let freeArea = cargoSpace.freeSpaceArea;
    if (this.unit === 'cm' || this.unit === 'mm') {
      freeArea = freeArea / 10000;
    }
    return freeArea;
  }

  public calculateTakenLdm(cargoSpace: CargoSpace): number {
    let takenLdm = this.getTakenLdmRaw(cargoSpace);
    if (this.unit === 'cm' || this.unit === 'mm') {
      takenLdm = takenLdm / 100;
    }
    return takenLdm;
  }

  public calculateFreeLdm(cargoSpace: CargoSpace): number {
    let freeLdm = cargoSpace.dimensions.length - this.getTakenLdmRaw(cargoSpace);

    if (cargoSpace.extraFeatures.varioTrailerFloorHeight) {
      freeLdm = cargoSpace.dimensions.length * 2 - this.getTakenLdmRaw(cargoSpace);
    }

    if (this.unit === 'cm' || this.unit === 'mm') {
      freeLdm = freeLdm / 100;
    }
    return freeLdm;
  }

  public calculateTakenLdmVarioUp(cargoSpace: CargoSpace): number {
    let takenLdmVarioUp = cargoSpace.ldmVarioUp;
    if (this.unit === 'cm' || this.unit === 'mm') {
      takenLdmVarioUp = takenLdmVarioUp / 100;
    }
    return takenLdmVarioUp;
  }

  public calculateFreeLdmVarioUp(cargoSpace: CargoSpace): number {
    let freeLdmVarioUp = cargoSpace.dimensions.length - cargoSpace.ldmVarioUp;

    if (this.unit === 'cm' || this.unit === 'mm') {
      freeLdmVarioUp = freeLdmVarioUp / 100;
    }
    return freeLdmVarioUp;
  }

  public countLoadedLoads(
    cargoSpace: CargoSpace,
    stops?: WorkSpace[],
    currentIndex?: number,
    onlyLoaded?: boolean,
  ): number {
    const groups = this.getSummaryGroups(cargoSpace);
    if (!stops || onlyLoaded) {
      return groups.reduce((sum, group) => sum + group.quantity, 0);
    } else {
      const allPreviousStops: WorkSpace[] = stops.slice(0, currentIndex);
      const allPreviousStopsLoads: Group[] = [];
      allPreviousStops.forEach((stop: WorkSpace): void => {
        stop.cargoSpaces.forEach((space: CargoSpace): void => {
          this.getSummaryGroups(space).forEach((group: Group): void => {
            allPreviousStopsLoads.push(group);
          });
        });
      });
      return groups.reduce((sum: number, group: Group) => {
        const isLoaded = allPreviousStopsLoads.some(
          (previousGroup: Group) => previousGroup.id === group.id,
        );
        return isLoaded ? sum : sum + group.quantity;
      }, 0);
    }
  }

  public calculateTotalWeight(cargoSpace: CargoSpace): number {
    return this.getSummaryGroups(cargoSpace).reduce(
      (sum, group) => sum + group.quantity * group.weight,
      0,
    );
  }

  public calculateTotalVolume(cargoSpace: CargoSpace): number {
    let totalVolume =
      cargoSpace.dimensions.length * cargoSpace.dimensions.width * cargoSpace.dimensions.height;

    if (cargoSpace.extraFeatures.varioTrailerFloorHeight) {
      totalVolume =
        totalVolume -
        cargoSpace.dimensions.length *
          cargoSpace.dimensions.width *
          cargoSpace.extraFeatures.varioTrailerFloorThickness;
    }
    if (this.unit === 'cm' || this.unit === 'mm') {
      totalVolume = totalVolume / 1000000;
    }
    return totalVolume;
  }

  public calculateOccupiedVolume(cargoSpace: CargoSpace) {
    let occupiedVolume = this.getSummaryGroups(cargoSpace).reduce(
      (sum, group) =>
        sum +
        group.quantity * group.dimensions.length * group.dimensions.width * group.dimensions.height,
      0,
    );
    if (this.unit === 'cm' || this.unit === 'mm') {
      occupiedVolume = occupiedVolume / 1000000;
    }
    return occupiedVolume;
  }

  public countNotLoadedLoads(outsideSpace: OutsideSpace): number {
    return outsideSpace.groups.reduce((sum: number, group) => sum + group.quantity, 0);
  }

  private getSummaryGroups(cargoSpace: CargoSpace): Group[] {
    return visualizationGroupsForCargoSpace(cargoSpace);
  }

  private getTakenLdmRaw(cargoSpace: CargoSpace): number {
    if (this.shouldCalculateLdmFromGroups(cargoSpace)) {
      return this.calculateOccupiedLdmFromGroups(cargoSpace);
    }
    return cargoSpace.ldm ?? 0;
  }

  private shouldCalculateLdmFromGroups(cargoSpace: CargoSpace): boolean {
    return (
      cargoSpace.type === LoadingSpaceType.Pallet && cargoSpaceHasLoadedPalletLayers(cargoSpace)
    );
  }

  private calculateOccupiedLdmFromGroups(cargoSpace: CargoSpace): number {
    const groundLevel = cargoSpace.position?.y ?? 0;
    const footprintSpans: { z: number; z2: number }[] = [];

    for (const group of this.getSummaryGroups(cargoSpace)) {
      for (const cube of group.loadsCubes ?? []) {
        if ((cube.position?.y ?? 0) !== groundLevel) {
          continue;
        }

        const z = cube.position?.z ?? 0;
        const dimensions = cube.dimensions ?? group.dimensions;
        const loadsPerAxisZ = cube.loadsPerAxis?.z ?? 1;
        const extent =
          group.loadType === 1 || group.loadType === 2
            ? (dimensions?.diameter ?? 0) * loadsPerAxisZ
            : (dimensions?.length ?? 0) * loadsPerAxisZ;

        footprintSpans.push({ z, z2: z + extent });
      }
    }

    if (!footprintSpans.length) {
      return cargoSpace.ldm ?? 0;
    }

    footprintSpans.sort((a, b) => a.z - b.z);

    let occupiedLdm = 0;
    let lastZ: number | null = null;
    for (const span of footprintSpans) {
      if (lastZ === null || span.z2 > lastZ) {
        occupiedLdm += span.z2 - Math.max(lastZ ?? 0, span.z);
        lastZ = span.z2;
      }
    }

    return Math.min(occupiedLdm, cargoSpace.dimensions.length);
  }

  private round(num: number) {
    return _.round(num, 2);
  }
}
